package com.starparty.app.astro0.renderers

import com.starparty.app.astro0.SP_Colour
import com.starparty.app.astro0.Sky
import com.starparty.app.astro0.UMS
import com.starparty.app.astro0.celestialbodies.CelestialBody
import com.starparty.app.astro0.celestialbodies.Star
import com.starparty.app.geometry.SP_Point
import com.starparty.app.geometry.SP_Rectangle
import kotlin.math.max

/**
 * Title:        Starparty Astro-0
 * Description:  Simply astronomy package for starparty.com and sennen-cove-cornwall.uk
 * Copyright:    Copyright (c) 2021
 * Company: starparty.com
 * @author Rob Leeson
 * @version 2.0
 */
class StarRenderer : CelestialBodyRenderer() {

        protected val ZERO_MAG_DIAMETER = 10

        fun getToolTipText(s: Star, sky: Sky): String {
            val equ = s.equatorialCoords
            val altAz = sky.EquToAltAzm(equ.rA, equ.dec)
            val D = "deg "
            val DM = "' "
            val DS = "\""
            val H = "h "
            val M = "m "
            val S = "s"
            val DP = 1 // Number of decimal places.
            val raHMS: String = UMS.degreesToHMS(equ.rA).toString(DP, H, M, S)
            val decDMS: String =
                UMS.degreesToDMS(equ.dec).toString(DP, D, DM, DS)
            val altDMS: String =
                UMS.degreesToDMS(altAz.alt).toString(DP, D, DM, DS)
            val azHMS: String =
                UMS.degreesToHMS(altAz.azm).toString(DP, H, DM, DS)
            return ("<HTML>" +
                    "Constellation: " + s.constellationName + "<BR>" +
                    "Flamstead: " + s.flamsteadNumber + " "
                    + s.constellationAbbr + "<BR>" +
                    "RA: " + raHMS + "<BR>" +
                    "DEC: " + decDMS + "<BR>" +
                    "ALT: " + altDMS + "<BR>" +
                    "AZM: " + azHMS + "<BR>" +
                    "Visual Magnitude: " + s.visMag +
                    "</HTML>")
        }

        fun getStatusBarText(s: Star, sky: Sky): String {
            val equ = s.equatorialCoords
            val altAz = sky.EquToAltAzm(equ.rA, equ.dec)
            val D = "deg "
            val DM = "' "
            val DS = "\""
            val H = "h "
            val M = "m "
            val S = "s"
            val SEP = ""
            val DP = 1 // Number of decimal places.
            val raHMS: String = UMS.degreesToHMS(equ.rA).toString(DP, H, M, S)
            val decDMS: String =
                UMS.degreesToDMS(equ.dec).toString(DP, D, DM, DS)
            val altDMS: String =
                UMS.degreesToDMS(altAz.alt).toString(DP, D, DM, DS)
            val azHMS: String =
                UMS.degreesToHMS(altAz.azm).toString(DP, H, DM, DS)
            return ("Constellation:" + s.constellationName + ", " +
                    "Flamstead:" + s.flamsteadNumber + ", "
                    + s.constellationAbbr + " " +
                    "RA: " + raHMS + ", " +
                    "DEC: " + decDMS + ", " +
                    "ALT: " + altDMS + ", " +
                    "AZM: " + azHMS + ", " +
                    "Visual Magnitude:" + s.visMag)
        }

        fun contains(s: Star, p: SP_Point): Boolean {
            val diameter = getDiameter(s)
            val halfDiam = diameter / 2
            val left = s.skyLocation.x - halfDiam
            val top = s.skyLocation.y - halfDiam
            val ovalBounds = SP_Rectangle(left, top, diameter, diameter)
            return ovalBounds.contains(p.x, p.y)
        }

        override fun getDiameter(b: CelestialBody): Double {
            var diameter = 50.0 // Todo Handle error properly

            if (b is Star)
            {
                diameter = ZERO_MAG_DIAMETER - 1.5 * b.visMag
            }

            return max(diameter, 1.0)
        }

        override fun getColour(c: CelestialBody):SP_Colour { return c.rgbColour }

}
